// WorldContext — единая точка доступа к save+world внутри save-scoped routes.
//
// Заполняется через SaveRouteGate. Дочерние страницы читают через хуки:
// useSave(), useWorld(), usePlots/Beds/Plantings(), useAllowedActions(target),
// useCanIDo(action).

import React, { createContext, useContext, useMemo } from 'react';
import type {
  SaveWithProjection,
  SaveWorld,
  SaveAllowedActions,
  SaveWorldEntities,
  WorldRole,
  WorldPlot,
  WorldBed,
  WorldPlanting,
  WorldField,
  WorldStructuralObject,
  CapabilitiesSnapshot,
} from '../types/saves';

export interface WorldContextValue {
  save: SaveWithProjection;
  world: SaveWorld;
  myRole: WorldRole;
  refresh: () => Promise<unknown>;
}

const WorldContext = createContext<WorldContextValue | null>(null);

export const WorldProvider: React.FC<{
  value: WorldContextValue;
  children: React.ReactNode;
}> = ({ value, children }) => (
  <WorldContext.Provider value={value}>{children}</WorldContext.Provider>
);

const useWorldContext = (): WorldContextValue => {
  const ctx = useContext(WorldContext);
  if (!ctx) {
    throw new Error('Component must be rendered inside <WorldProvider>');
  }
  return ctx;
};

// --- public hooks ---

export const useSave = (): SaveWithProjection => useWorldContext().save;

/** Use within save-scoped routes only. Returns null if context unavailable. */
export const useSaveOptional = (): SaveWithProjection | null => {
  const ctx = useContext(WorldContext);
  return ctx?.save ?? null;
};

export const useWorld = (): SaveWorld => useWorldContext().world;

/** Use within save-scoped routes only. Returns null if context unavailable. */
export const useWorldOptional = (): SaveWorld | null => {
  const ctx = useContext(WorldContext);
  return ctx?.world ?? null;
};

export const useWorldEntities = (): SaveWorldEntities => useWorldContext().world.structure;

export const useFields = (): WorldField[] => useWorldEntities().fields;
export const usePlots = (): WorldPlot[] => useWorldEntities().plots;
export const useBeds = (): WorldBed[] => useWorldEntities().beds;
export const usePlantings = (): WorldPlanting[] => useWorldEntities().plantings;
export const useStructuralObjects = (): WorldStructuralObject[] =>
  useWorldEntities().structuralObjects;

export const useMyRole = (): WorldRole => useWorldContext().myRole;
export const useFarmIdFromSave = (): string | null => useWorldContext().world.farm.id;

export const useCapabilities = (): CapabilitiesSnapshot | null =>
  useWorldContext().world.capabilities;

export const useAllowedActions = (
  target?: { kind: string; id: string }
): string[] => {
  const allowed: SaveAllowedActions = useWorldContext().world.allowedActions;
  const targetKey = target ? `${target.kind}:${target.id}` : null;

  return useMemo(() => {
    if (!targetKey) return allowed.global || [];
    return allowed.byTarget?.[targetKey] || [];
  }, [allowed, targetKey]);
};

export const useCanIDo = (
  action: string,
  target?: { kind: string; id: string }
): boolean => {
  const allowed = useAllowedActions(target);
  return allowed.includes(action);
};

export const useRefreshWorld = (): (() => Promise<unknown>) =>
  useWorldContext().refresh;

/** Use within save-scoped routes only. Returns null if context unavailable. */
export const useRefreshWorldOptional = (): (() => Promise<unknown>) | null => {
  const ctx = useContext(WorldContext);
  return ctx?.refresh ?? null;
};
